import { z } from "zod";
import {
  type ActionMessage,
  actionMessage,
  type Reducer,
  type UnconfirmedActionMessage,
  unconfirmedActionMessage,
} from "./core";
import type { RoundAggregate } from "./round/aggregate";

const performActionSchema = z.object({
  type: z.literal("PerformAction"),
  action: unconfirmedActionMessage,
});

export type PerformAction = z.infer<typeof performActionSchema>;

const registerSchema = z.object({
  type: z.literal("Register"),
  lastConfirmedVersion: z.number(),
});

export type Register = z.infer<typeof registerSchema>;

// DESIGN: 'To' semantics over 'From' semantics
// This will be nicer if we ever have to send a message from a server to
// another server
export const messageToServerSchema = z.discriminatedUnion("type", [
  performActionSchema,
  registerSchema,
]);
export type MessageToServer = z.infer<typeof messageToServerSchema>;

const performActionFailedSchema = z.object({
  type: z.literal("PerformActionFailed"),
  message: z.string(),
  actionId: z.string(),
});

export type PerformActionFailed = z.infer<typeof performActionFailedSchema>;

const confirmActionSchema = z.object({
  type: z.literal("ConfirmActions"),
  actions: z.array(actionMessage),
});

export type ConfirmAction = z.infer<typeof confirmActionSchema>;

export const messageToClientSchema = z.discriminatedUnion("type", [
  performActionFailedSchema,
  confirmActionSchema,
]);

export type MessageToClient = z.infer<typeof messageToClientSchema>;

export interface ClientStorageAdapter {
  performUnconfirmedAction(
    reducer: Reducer,
    action: UnconfirmedActionMessage,
  ): Promise<null | Error>;

  performConfirmedAction(
    reducer: Reducer,
    action: ActionMessage,
  ): Promise<void | Error | ActionError[]>;

  getConfirmedActions(): ActionMessage[];

  getAction(actionId: string): Promise<UnconfirmedActionMessage | null>;

  getAggregateAtVersion(
    reducer: Reducer,
    version: number,
  ): Promise<RoundAggregate | null>;

  getConfirmedAggregateSync(): RoundAggregate;
  getUnconfirmedAggregateSync(): RoundAggregate;

  getLastConfirmedVersion(): number;

  getUnconfirmedActions(): Promise<
    {
      createdAt: number;
      message: UnconfirmedActionMessage;
    }[]
  >;

  clearUnconfirmedActions(): Promise<void>;

  getAggregateBeforeUnconfirmedAction(
    reducer: Reducer,
    actionId: string,
  ): Promise<RoundAggregate | Error>;
}

export type ActionError = {
  actionId: string;
  error: Error;
};

export interface ServerStorageAdapter {
  performAction(action: UnconfirmedActionMessage): Promise<ActionMessage>;

  getConfirmedActionsSince(
    aggregateId: string,
    version: number,
  ): ActionMessage[];

  getLastConfirmedAggregate(aggregateId: string): RoundAggregate | null;
}
